import React, { useState, useEffect } from 'react';
import { getFirestore, doc, getDocs, collection, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import Swal from 'sweetalert2';

const templatePresets = {
  modern: {
    primaryColor: '#ff5722',
    secondaryColor: '#2196f3',
    backgroundColor: '#ffffff',
    textColor: '#333333',
    accentColor: '#ff9800',
    fontFamily: 'modern',
    borderRadius: 'large',
    headerStyle: 'banner',
    headerTextColor: '#ffffff',
    productLayout: 'grid',
    categoryNavStyle: 'tabs',
  },
  classic: {
    primaryColor: '#1a1a1a',
    secondaryColor: '#8b7355',
    backgroundColor: '#faf8f5',
    textColor: '#2c2c2c',
    accentColor: '#c9a96e',
    fontFamily: 'classic',
    borderRadius: 'small',
    headerStyle: 'minimal',
    headerTextColor: '#ffffff',
    productLayout: 'list',
    categoryNavStyle: 'tabs',
  },
  bistro: {
    primaryColor: '#5d4037',
    secondaryColor: '#8d6e63',
    backgroundColor: '#fff8e1',
    textColor: '#3e2723',
    accentColor: '#e65100',
    fontFamily: 'modern',
    borderRadius: 'large',
    headerStyle: 'compact',
    headerTextColor: '#ffffff',
    productLayout: 'cards',
    categoryNavStyle: 'pills',
  },
};

const defaultDesign = {
  template: 'modern',
  primaryColor: '#ff5722',
  secondaryColor: '#2196f3',
  backgroundColor: '#ffffff',
  textColor: '#333333',
  accentColor: '#ff9800',
  logoUrl: '',
  logoPosition: 'center',
  headerStyle: 'banner',
  headerTextColor: '#ffffff',
  headerImageUrl: '',
  productLayout: 'grid',
  showProductImages: true,
  fontFamily: 'modern',
  borderRadius: 'large',
  categoryNavStyle: 'tabs',
};

const fontMap = {
  modern: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  classic: "'Georgia', 'Times New Roman', serif",
  playful: "'Comic Sans MS', 'Chalkboard SE', sans-serif",
};

const borderRadiusMap = {
  none: '0px',
  small: '4px',
  large: '16px',
};

const SiteDesignTab = ({ user, location, publicStore, setPublicStore }) => {
  const [loading, setLoading] = useState(true);
  const [menuData, setMenuData] = useState({ categories: [], products: [], options: [] });
  const [design, setDesign] = useState(defaultDesign);
  const [saving, setSaving] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [previewDevice, setPreviewDevice] = useState('desktop');

  useEffect(() => {
    if (publicStore && location) {
      loadMenuData();
      const savedDesign = publicStore.design || {};
      const mergedDesign = {
        ...defaultDesign,
        ...savedDesign,
        logoUrl: savedDesign.logoUrl || publicStore.logoUrl || location?.logoUrl || '',
      };
      setDesign(mergedDesign);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicStore, location]);

  const applyTemplate = (templateKey) => {
    const preset = templatePresets[templateKey] || templatePresets.modern;
    setDesign((prev) => ({ ...prev, template: templateKey, ...preset }));
  };

  const handleResetToDefault = () => {
    const resetDesign = {
      ...defaultDesign,
      logoUrl: publicStore?.logoUrl || location?.logoUrl || '',
    };
    setDesign(resetDesign);
    Swal.fire({
      title: 'Reset!',
      text: 'Design settings have been reset to defaults. Click Save Design to apply.',
      icon: 'info',
      timer: 2000,
      showConfirmButton: false,
    });
  };

  const loadMenuData = async () => {
    setLoading(true);
    try {
      const db = getFirestore();
      const locationId = location.id;
      const catsSnap = await getDocs(collection(db, 'publicStores', locationId, 'categories'));
      const categories = [];
      const products = [];
      for (const catDoc of catsSnap.docs) {
        const cat = { id: catDoc.id, ...catDoc.data() };
        categories.push(cat);
        const prodSnap = await getDocs(collection(db, 'publicStores', locationId, 'categories', cat.id, 'products'));
        prodSnap.docs.forEach((p) => products.push({ id: p.id, categoryId: cat.id, ...p.data() }));
      }
      const optsSnap = await getDocs(collection(db, 'publicStores', locationId, 'productOptions'));
      const options = optsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setMenuData({ categories, products, options });
      if (categories.length > 0) setActiveCategory(categories[0].id);
    } catch (error) {
      console.error('Error loading menu data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDesign = async () => {
    if (!publicStore || !location) return;
    setSaving(true);
    try {
      const db = getFirestore();
      await updateDoc(doc(db, 'publicStores', location.id), {
        design,
        updatedAt: serverTimestamp(),
      });
      setPublicStore({ ...publicStore, design });
      Swal.fire('Saved!', 'Store design has been updated.', 'success');
    } catch (error) {
      console.error('Error saving design:', error);
      Swal.fire('Error', 'Failed to save design.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleHeaderImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !location) return;

    if (!file.type.startsWith('image/')) {
      Swal.fire('Error', 'Please select an image file (JPG, PNG, etc.)', 'error');
      return;
    }

    setSaving(true);
    try {
      const storage = getStorage();
      const storageRef = ref(storage, `publicStores/${location.id}/header-images/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);
      setDesign((prev) => ({ ...prev, headerImageUrl: downloadUrl }));
      Swal.fire('Uploaded!', 'Header background image has been uploaded.', 'success');
    } catch (error) {
      console.error('Error uploading header image:', error);
      Swal.fire('Error', 'Failed to upload image. ' + error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveHeaderImage = () => {
    setDesign((prev) => ({ ...prev, headerImageUrl: '' }));
  };

  const getCategoryProducts = (catId) => menuData.products.filter((p) => p.categoryId === catId);
  const formatCurrency = (val) => {
    const num = typeof val === 'number' ? val : parseFloat(val) || 0;
    return `$${num.toFixed(2)}`;
  };

  const previewStyle = {
    fontFamily: fontMap[design.fontFamily] || fontMap.modern,
    backgroundColor: design.backgroundColor,
    color: design.textColor,
    borderRadius: borderRadiusMap[design.borderRadius] || borderRadiusMap.large,
    overflow: 'hidden',
    border: '1px solid #ddd',
  };

  const headerStyle = () => {
    const base = {
      backgroundColor: design.primaryColor,
      color: design.headerTextColor || '#ffffff',
      padding: design.headerStyle === 'minimal' ? '16px' : design.headerStyle === 'compact' ? '24px' : '48px 24px',
      textAlign: design.logoPosition === 'center' ? 'center' : design.logoPosition === 'right' ? 'right' : 'left',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    };
    if (design.headerImageUrl) {
      base.backgroundImage = `url(${design.headerImageUrl})`;
    } else if (design.headerStyle === 'banner') {
      base.background = `linear-gradient(135deg, ${design.primaryColor} 0%, ${design.secondaryColor} 100%)`;
    }
    return base;
  };

  const renderHeader = () => {
    const logoToShow = design.logoUrl || publicStore?.logoUrl || location?.logoUrl || '';
    const isSideBySide = design.logoPosition === 'left' || design.logoPosition === 'right';
    return (
      <div style={headerStyle()}>
        {isSideBySide ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexDirection: design.logoPosition === 'right' ? 'row-reverse' : 'row', justifyContent: design.logoPosition === 'left' ? 'flex-start' : 'flex-end' }}>
            {logoToShow && (
              <img src={logoToShow} alt="Logo" style={{ maxHeight: design.template === 'bistro' ? 80 : 60, borderRadius: design.template === 'bistro' ? 50 : 8, flexShrink: 0 }} />
            )}
            <div style={{ textAlign: design.logoPosition }}>
              <h2 style={{ margin: 0, fontWeight: design.template === 'classic' ? 400 : 700, fontSize: design.headerStyle === 'compact' ? '1.5rem' : '2rem', letterSpacing: design.template === 'classic' ? '1px' : 'normal' }}>
                {publicStore?.locationName || location?.companyName || 'Your Store'}
              </h2>
              <p style={{ margin: '8px 0 0', opacity: 0.9, fontSize: '0.95rem', fontStyle: design.template === 'classic' ? 'italic' : 'normal' }}>
                {publicStore?.address || ''}
              </p>
            </div>
          </div>
        ) : (
          <>
            {logoToShow && (
              <img src={logoToShow} alt="Logo" style={{ maxHeight: design.template === 'bistro' ? 80 : 60, marginBottom: 12, borderRadius: design.template === 'bistro' ? 50 : 8 }} />
            )}
            <h2 style={{ margin: 0, fontWeight: design.template === 'classic' ? 400 : 700, fontSize: design.headerStyle === 'compact' ? '1.5rem' : '2rem', letterSpacing: design.template === 'classic' ? '1px' : 'normal' }}>
              {publicStore?.locationName || location?.companyName || 'Your Store'}
            </h2>
            <p style={{ margin: '8px 0 0', opacity: 0.9, fontSize: '0.95rem', fontStyle: design.template === 'classic' ? 'italic' : 'normal' }}>
              {publicStore?.address || ''}
            </p>
          </>
        )}
      </div>
    );
  };

  const renderNav = () => {
    const isMobile = previewDevice === 'mobile';

    if (design.categoryNavStyle === 'sidebar' && !isMobile) {
      return (
        <div style={{ width: 180, flexShrink: 0, alignSelf: 'flex-start', position: 'sticky', top: 0, maxHeight: '100%', overflowY: 'auto', borderRight: '1px solid #eee', padding: 16, background: design.template === 'classic' ? '#f5f5f5' : '#fafafa' }}>
          {menuData.categories.map((cat) => (
            <div
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              style={{
                padding: '10px 12px',
                cursor: 'pointer',
                borderRadius: design.template === 'bistro' ? 20 : 6,
                marginBottom: 4,
                fontWeight: activeCategory === cat.id ? 700 : 400,
                background: activeCategory === cat.id ? design.primaryColor : 'transparent',
                color: activeCategory === cat.id ? '#fff' : design.textColor,
                fontFamily: fontMap[design.fontFamily],
              }}
            >
              {cat.name}
            </div>
          ))}
        </div>
      );
    }
    if (design.categoryNavStyle === 'pills' || (design.categoryNavStyle === 'sidebar' && isMobile)) {
      return (
        <div style={{ position: 'sticky', top: 0, zIndex: 10, padding: '16px 24px', background: design.template === 'classic' ? '#f5f5f5' : '#fafafa', borderBottom: '1px solid #eee' }}>
          <div style={{ display: 'flex', gap: 8, overflowX: 'auto', flexWrap: 'nowrap' }}>
            {menuData.categories.map((cat) => (
              <span
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                style={{
                  display: 'inline-block',
                  padding: '8px 16px',
                  cursor: 'pointer',
                  borderRadius: design.template === 'bistro' ? 24 : 20,
                  fontWeight: activeCategory === cat.id ? 700 : 400,
                  background: activeCategory === cat.id ? design.primaryColor : design.template === 'classic' ? '#e8e4df' : '#e0e0e0',
                  color: activeCategory === cat.id ? '#fff' : design.textColor,
                  whiteSpace: 'nowrap',
                  fontFamily: fontMap[design.fontFamily],
                  flexShrink: 0,
                }}
              >
                {cat.name}
              </span>
            ))}
          </div>
        </div>
      );
    }
    // tabs
    return (
      <div style={{ position: 'sticky', top: 0, zIndex: 10, display: 'flex', borderBottom: `2px solid ${design.primaryColor}`, background: design.template === 'classic' ? '#f5f5f5' : '#fafafa', overflowX: 'auto' }}>
        {menuData.categories.map((cat) => (
          <div
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            style={{
              padding: '12px 20px',
              cursor: 'pointer',
              fontWeight: activeCategory === cat.id ? 700 : 500,
              borderBottom: activeCategory === cat.id ? `3px solid ${design.primaryColor}` : '3px solid transparent',
              color: activeCategory === cat.id ? design.primaryColor : design.textColor,
              whiteSpace: 'nowrap',
              fontFamily: fontMap[design.fontFamily],
              flexShrink: 0,
            }}
          >
            {cat.name}
          </div>
        ))}
      </div>
    );
  };

  const renderProduct = (product) => {
    const opts = (product.optionIds || [])
      .map((oid) => menuData.options.find((o) => o.id === oid))
      .filter(Boolean);
    const br = borderRadiusMap[design.borderRadius] || 16;

    if (design.productLayout === 'list') {
      return (
        <div key={product.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0', borderBottom: `1px solid ${design.template === 'classic' ? '#e0e0e0' : '#eee'}` }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: design.template === 'classic' ? 500 : 600, fontSize: '1.05rem', fontFamily: fontMap[design.fontFamily] }}>{product.name}</div>
            {product.description && <div style={{ fontSize: '0.85rem', opacity: 0.7, marginTop: 2, fontStyle: design.template === 'classic' ? 'italic' : 'normal' }}>{product.description}</div>}
            {opts.length > 0 && <div style={{ fontSize: '0.75rem', color: design.secondaryColor, marginTop: 4 }}>Options available</div>}
          </div>
          <div style={{ fontWeight: design.template === 'classic' ? 400 : 700, fontSize: '1.1rem', color: design.accentColor, marginLeft: 16, fontFamily: fontMap[design.fontFamily] }}>
            {formatCurrency(product.price)}
          </div>
        </div>
      );
    }

    if (design.productLayout === 'grid') {
      return (
        <div
          key={product.id}
          style={{
            background: design.template === 'classic' ? 'transparent' : '#fff',
            borderRadius: br,
            overflow: 'hidden',
            boxShadow: design.template === 'classic' ? 'none' : design.template === 'bistro' ? '0 4px 12px rgba(0,0,0,0.1)' : '0 2px 8px rgba(0,0,0,0.06)',
            border: design.template === 'classic' ? `1px solid ${design.secondaryColor}40` : '1px solid #f0f0f0',
          }}
        >
          {design.showProductImages && product.imageUrl && (
            <div style={{ height: 140, backgroundImage: `url(${product.imageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center', borderRadius: design.template === 'classic' ? `${br} ${br} 0 0` : 0 }} />
          )}
          <div style={{ padding: 14 }}>
            <div style={{ fontWeight: design.template === 'classic' ? 500 : 600, fontSize: '0.95rem', marginBottom: 4, fontFamily: fontMap[design.fontFamily] }}>{product.name}</div>
            {product.description && <div style={{ fontSize: '0.8rem', opacity: 0.65, marginBottom: 8, lineHeight: 1.3, fontStyle: design.template === 'classic' ? 'italic' : 'normal' }}>{product.description}</div>}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, color: design.accentColor, fontSize: '1rem', fontFamily: fontMap[design.fontFamily] }}>{formatCurrency(product.price)}</span>
              {opts.length > 0 && <span style={{ fontSize: '0.7rem', color: design.secondaryColor, background: `${design.secondaryColor}15`, padding: '2px 8px', borderRadius: 10 }}>Customizable</span>}
            </div>
          </div>
        </div>
      );
    }

    // cards (default)
    return (
      <div
        key={product.id}
        style={{
          background: design.template === 'classic' ? 'transparent' : '#fff',
          borderRadius: br,
          padding: 16,
          boxShadow: design.template === 'classic' ? 'none' : design.template === 'bistro' ? '0 4px 12px rgba(0,0,0,0.1)' : '0 2px 12px rgba(0,0,0,0.08)',
          border: design.template === 'classic' ? `1px solid ${design.secondaryColor}40` : '1px solid #f5f5f5',
          display: 'flex',
          gap: 14,
          alignItems: 'flex-start',
        }}
      >
        {design.showProductImages && product.imageUrl && (
          <img src={product.imageUrl} alt={product.name} style={{ width: design.template === 'bistro' ? 100 : 80, height: design.template === 'bistro' ? 100 : 80, objectFit: 'cover', borderRadius: design.template === 'bistro' ? 50 : 10 }} />
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: design.template === 'classic' ? 500 : 700, fontSize: '1rem', marginBottom: 4, fontFamily: fontMap[design.fontFamily] }}>{product.name}</div>
          {product.description && <div style={{ fontSize: '0.85rem', opacity: 0.65, marginBottom: 8, fontStyle: design.template === 'classic' ? 'italic' : 'normal' }}>{product.description}</div>}
          {opts.length > 0 && <div style={{ fontSize: '0.75rem', color: design.secondaryColor, marginBottom: 8 }}>Options available</div>}
          <div style={{ fontWeight: 700, color: design.accentColor, fontSize: '1.1rem', fontFamily: fontMap[design.fontFamily] }}>{formatCurrency(product.price)}</div>
        </div>
      </div>
    );
  };

  const renderPreview = () => {
    const activeProds = activeCategory ? getCategoryProducts(activeCategory) : [];
    const isGrid = design.productLayout === 'grid';
    const isSidebar = design.categoryNavStyle === 'sidebar' && previewDevice === 'desktop';
    const isMobile = previewDevice === 'mobile';

    const wrapperStyle = {
      border: '2px dashed #ccc',
      borderRadius: 12,
      overflow: 'hidden',
      background: '#f8f9fa',
      maxWidth: isMobile ? 375 : '100%',
      margin: '0 auto',
      transition: 'max-width 0.3s ease',
      maxHeight: 'calc(100vh - 200px)',
      display: 'flex',
      flexDirection: 'column',
    };

    const scrollAreaStyle = {
      overflowY: 'auto',
      flex: 1,
    };

    return (
      <div style={wrapperStyle}>
        <div style={{ background: '#333', color: '#fff', padding: '6px 12px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ff5f56' }}></div>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ffbd2e' }}></div>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#27c93f' }}></div>
          <span className="ml-2">{isMobile ? 'Mobile Preview (375px)' : 'Desktop Preview'}</span>
        </div>
        <div style={{ ...previewStyle, overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div style={scrollAreaStyle}>
            {renderHeader()}
            {isSidebar ? (
              <div style={{ display: 'flex', flexDirection: 'row' }}>
                {renderNav()}
                <div style={{ flex: 1, padding: isMobile ? 12 : 24 }}>
                  {activeCategory && (
                    <>
                      <h3 style={{ margin: '0 0 16px', fontWeight: design.template === 'classic' ? 400 : 700, color: design.primaryColor, fontFamily: fontMap[design.fontFamily], fontSize: isMobile ? '1.2rem' : '1.5rem' }}>
                        {menuData.categories.find((c) => c.id === activeCategory)?.name}
                      </h3>
                      <div style={{
                        display: isGrid ? 'grid' : 'flex',
                        gridTemplateColumns: isGrid ? `repeat(auto-fill, minmax(${isMobile ? 140 : 180}px, 1fr))` : undefined,
                        gap: isGrid ? 16 : 12,
                        flexDirection: isGrid ? undefined : 'column',
                      }}>
                        {activeProds.map(renderProduct)}
                      </div>
                      {activeProds.length === 0 && (
                        <p style={{ opacity: 0.5, textAlign: 'center', padding: 40 }}>No products in this category.</p>
                      )}
                    </>
                  )}
                </div>
              </div>
            ) : (
              <>
                {renderNav()}
                <div style={{ padding: isMobile ? 12 : 24 }}>
                  {activeCategory && (
                    <>
                      <h3 style={{ margin: '0 0 16px', fontWeight: design.template === 'classic' ? 400 : 700, color: design.primaryColor, fontFamily: fontMap[design.fontFamily], fontSize: isMobile ? '1.2rem' : '1.5rem' }}>
                        {menuData.categories.find((c) => c.id === activeCategory)?.name}
                      </h3>
                      <div style={{
                        display: isGrid ? 'grid' : 'flex',
                        gridTemplateColumns: isGrid ? `repeat(auto-fill, minmax(${isMobile ? 140 : 180}px, 1fr))` : undefined,
                        gap: isGrid ? 16 : 12,
                        flexDirection: isGrid ? undefined : 'column',
                      }}>
                        {activeProds.map(renderProduct)}
                      </div>
                      {activeProds.length === 0 && (
                        <p style={{ opacity: 0.5, textAlign: 'center', padding: 40 }}>No products in this category.</p>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (!publicStore) {
    return (
      <div className="alert alert-warning">
        Please deploy your store in the <strong>Site Information</strong> tab before designing it.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
        <div className="spinner-border text-orange" role="status"><span className="sr-only">Loading...</span></div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-lg-5">
          <div className="card card-outline card-primary">
            <div className="card-header">
              <h3 className="card-title"><i className="fas fa-paint-brush mr-2"></i>Design Settings</h3>
            </div>
            <div className="card-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
              <div className="form-group">
                <label>Template</label>
                <select className="form-control" value={design.template} onChange={(e) => applyTemplate(e.target.value)}>
                  <option value="modern">Modern (Clean & Vibrant)</option>
                  <option value="classic">Classic (Elegant & Simple)</option>
                  <option value="bistro">Bistro (Warm & Cozy)</option>
                </select>
                <small className="form-text text-muted">Changing the template updates colors, fonts, and layout presets automatically.</small>
              </div>

              <div className="row">
                <div className="col-6 form-group">
                  <label>Primary Color</label>
                  <input type="color" className="form-control" style={{ height: 40 }} value={design.primaryColor} onChange={(e) => setDesign({ ...design, primaryColor: e.target.value })} />
                </div>
                <div className="col-6 form-group">
                  <label>Secondary Color</label>
                  <input type="color" className="form-control" style={{ height: 40 }} value={design.secondaryColor} onChange={(e) => setDesign({ ...design, secondaryColor: e.target.value })} />
                </div>
              </div>
              <div className="row">
                <div className="col-6 form-group">
                  <label>Background</label>
                  <input type="color" className="form-control" style={{ height: 40 }} value={design.backgroundColor} onChange={(e) => setDesign({ ...design, backgroundColor: e.target.value })} />
                </div>
                <div className="col-6 form-group">
                  <label>Text Color</label>
                  <input type="color" className="form-control" style={{ height: 40 }} value={design.textColor} onChange={(e) => setDesign({ ...design, textColor: e.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label>Accent Color</label>
                <input type="color" className="form-control" style={{ height: 40 }} value={design.accentColor} onChange={(e) => setDesign({ ...design, accentColor: e.target.value })} />
              </div>

              <div className="form-group">
                <label>Header Text Color</label>
                <input type="color" className="form-control" style={{ height: 40 }} value={design.headerTextColor} onChange={(e) => setDesign({ ...design, headerTextColor: e.target.value })} />
                <small className="form-text text-muted">Color of store name and address text in the header.</small>
              </div>

              <div className="form-group">
                <label>Logo URL</label>
                <input type="text" className="form-control" value={design.logoUrl || publicStore?.logoUrl || location?.logoUrl || ''} onChange={(e) => setDesign({ ...design, logoUrl: e.target.value })} placeholder="https://..." />
                {(design.logoUrl || publicStore?.logoUrl || location?.logoUrl) && (
                  <div className="mt-2">
                    <img src={design.logoUrl || publicStore?.logoUrl || location?.logoUrl} alt="Logo preview" style={{ maxHeight: 50, maxWidth: 180, borderRadius: 6, border: '1px solid #ddd' }} />
                  </div>
                )}
                <small className="form-text text-muted">Leave blank to use the logo from Site Information.</small>
              </div>
              <div className="form-group">
                <label>Logo Position</label>
                <select className="form-control" value={design.logoPosition} onChange={(e) => setDesign({ ...design, logoPosition: e.target.value })}>
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </div>

              <div className="form-group">
                <label>Header Style</label>
                <select className="form-control" value={design.headerStyle} onChange={(e) => setDesign({ ...design, headerStyle: e.target.value })}>
                  <option value="banner">Banner (Large gradient)</option>
                  <option value="compact">Compact</option>
                  <option value="minimal">Minimal</option>
                </select>
              </div>

              <div className="form-group">
                <label>Header Background Image</label>
                <div className="custom-file">
                  <input type="file" className="custom-file-input" id="headerImageUpload" accept="image/*" onChange={handleHeaderImageUpload} />
                  <label className="custom-file-label" htmlFor="headerImageUpload">Choose image...</label>
                </div>
                <small className="form-text text-muted">Upload a JPG/PNG image for the store header background. This will override header colors.</small>

                {design.headerImageUrl && (
                  <div className="mt-2">
                    <img src={design.headerImageUrl} alt="Header preview" style={{ maxHeight: 80, maxWidth: '100%', borderRadius: 6, border: '1px solid #ddd' }} />
                    <button className="btn btn-sm btn-outline-danger mt-1" onClick={handleRemoveHeaderImage}>
                      <i className="fas fa-trash mr-1"></i>Remove Image
                    </button>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Header Image URL (optional override)</label>
                <input type="text" className="form-control" value={design.headerImageUrl || ''} onChange={(e) => setDesign({ ...design, headerImageUrl: e.target.value })} placeholder="https://your-cdn.com/header-bg.jpg" />
                <small className="form-text text-muted">If you prefer, paste an external image URL directly. Leave blank to use colors.</small>
              </div>

              <div className="form-group">
                <label>Product Layout</label>
                <select className="form-control" value={design.productLayout} onChange={(e) => setDesign({ ...design, productLayout: e.target.value })}>
                  <option value="cards">Cards (Detailed)</option>
                  <option value="grid">Grid (Image focused)</option>
                  <option value="list">List (Compact)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Category Navigation</label>
                <select className="form-control" value={design.categoryNavStyle} onChange={(e) => setDesign({ ...design, categoryNavStyle: e.target.value })}>
                  <option value="tabs">Tabs</option>
                  <option value="pills">Pills</option>
                  <option value="sidebar">Sidebar</option>
                </select>
              </div>

              <div className="form-group">
                <label>Font Style</label>
                <select className="form-control" value={design.fontFamily} onChange={(e) => setDesign({ ...design, fontFamily: e.target.value })}>
                  <option value="modern">Modern (Sans-serif)</option>
                  <option value="classic">Classic (Serif)</option>
                  <option value="playful">Playful</option>
                </select>
              </div>

              <div className="form-group">
                <label>Card Rounding</label>
                <select className="form-control" value={design.borderRadius} onChange={(e) => setDesign({ ...design, borderRadius: e.target.value })}>
                  <option value="none">None</option>
                  <option value="small">Small</option>
                  <option value="large">Large</option>
                </select>
              </div>

              <div className="form-group">
                <div className="custom-control custom-switch">
                  <input type="checkbox" className="custom-control-input" id="showImages" checked={design.showProductImages} onChange={(e) => setDesign({ ...design, showProductImages: e.target.checked })} />
                  <label className="custom-control-label" htmlFor="showImages">Show product images</label>
                </div>
              </div>

              <button className="btn btn-primary btn-block" onClick={handleSaveDesign} disabled={saving}>
                {saving ? (
                  <><span className="spinner-border spinner-border-sm mr-2"></span>Saving...</>
                ) : (
                  <><i className="fas fa-save mr-2"></i>Save Design</>
                )}
              </button>
              <button className="btn btn-outline-secondary btn-block mt-2" onClick={handleResetToDefault}>
                <i className="fas fa-undo mr-2"></i>Reset to Default
              </button>
            </div>
          </div>
        </div>

        <div className="col-lg-7">
          <div className="d-flex justify-content-center mb-3">
            <div className="btn-group" role="group">
              <button
                type="button"
                className={`btn btn-${previewDevice === 'desktop' ? 'primary' : 'outline-secondary'}`}
                onClick={() => setPreviewDevice('desktop')}
              >
                <i className="fas fa-desktop mr-2"></i>Desktop
              </button>
              <button
                type="button"
                className={`btn btn-${previewDevice === 'mobile' ? 'primary' : 'outline-secondary'}`}
                onClick={() => setPreviewDevice('mobile')}
              >
                <i className="fas fa-mobile-alt mr-2"></i>Mobile
              </button>
            </div>
          </div>
          {renderPreview()}
        </div>
      </div>
    </div>
  );
};

export default SiteDesignTab;
